import { useApi } from '@src/app/context/Api';
import { User } from '@src/entities/User/ui/components/User';
import { Button } from '@src/shared/ui/components/Button';
import { useToast } from '@uhg-abyss/web/hooks/useToast';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { isUndefined } from 'lodash';
import React, { useEffect, useLayoutEffect, useState } from 'react';

/**
 * Re-Evaluate
 *
 * Provides the user with a button to re-evaluate the action path.
 *
 * @param actionPath
 * @returns {Element}
 * @constructor
 */
export function ReEvaluate({ actionPath = {} }) {
  const [isReEvaluateButtonDisabled, setIsReEvaluateButtonDisabled] = useState(false);
  const { toast } = useToast();

  const { clearApiCache, useApiMutation, useApiQuery } = useApi('ActionPath');

  const [GetActionPathTrackerStatus, { data: actionPathTrackerStatus }] = useApiQuery('GetActionPathTrackerStatus');
  const [ReEvaluateActionPath] = useApiMutation('ReEvaluateActionPath');

  /**
   * Fetch the action path tracker status if it hasn't been loaded yet.
   */
  useLayoutEffect(() => {
    if (isUndefined(actionPathTrackerStatus) && actionPath?.manualAssociation === false) {
      GetActionPathTrackerStatus({ actionPathId: actionPath?.id });
    }
  }, [actionPath, actionPathTrackerStatus]);

  /**
   * handleReEvaluation
   *
   * Handle the re-evaluation of the action path.
   *
   * @returns {Promise<void>}
   */
  const handleReEvaluation = async () => {
    const toastId = 'ReEvaluateActionPath';

    toast.show({
      autoClose: true,
      id: `${toastId}-info`,
      isPending: true,
      message: `The action path is preparing to schedule a re-evaluation.`,
      title: `Scheduling Action Path Re-Evaluation...`,
      variant: 'info',
    });

    setIsReEvaluateButtonDisabled(true);

    await ReEvaluateActionPath(
      {
        actionPathId: actionPath?.id,
      },
      {
        onError: () => {
          toast.hide(`${toastId}-info`);
          toast.show({
            id: `${toastId}-error`,
            message: `There was an error re-evaluating the action path.`,
            title: `Failed to Re-Evaluate Action Path`,
            variant: 'error',
          });
          setIsReEvaluateButtonDisabled(false);
        },
        onSuccess: async () => {
          toast.hide(`${toastId}-info`);
          toast.show({
            id: `${toastId}-success`,
            message: 'Action Path has been scheduled for re-evaluation.',
            title: `Action Path Re-Evaluation Scheduled`,
            variant: 'success',
          });
          await clearApiCache([{ apiKey: 'ActionPath', queryKeys: ['GetActionPathTrackerStatus'] }]);
          setIsReEvaluateButtonDisabled(true);
        },
      }
    );
  };

  /**
   * Disable the re-evaluate button if the action path tracker status is not complete.
   */
  useEffect(() => {
    if (!isUndefined(actionPathTrackerStatus) && actionPathTrackerStatus?.actionPathTrackerStatus !== 'COMPLETE') {
      setIsReEvaluateButtonDisabled(true);
    }
  }, [actionPathTrackerStatus?.actionPathTrackerStatus]);

  return (
    <User.Capability attributes={['reevaluate']} resource="ActionPaths">
      <Button
        before={<IconSymbol icon="refresh" variant="outlined" />}
        isDisabled={isReEvaluateButtonDisabled}
        onClick={async () => {
          await handleReEvaluation();
        }}
        variant="solid"
      >
        Re-Evaluate
      </Button>
    </User.Capability>
  );
}
